const STORAGE_KEY = "conversation:createdApplets"
const MAX_ENTRIES = 10
const STALE_AFTER_MS = 2 * 24 * 60 * 60 * 1000

export type CreatedAppletEntry = {
  conversationId: string | null
  appletId: string
  createdAt: number
}

type RecordInput = {
  conversationId: string | null
  appletId: string
}

const getStorage = (): Storage | null => {
  if (typeof window === "undefined") return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

const parseEntries = (raw: string | null): CreatedAppletEntry[] => {
  if (!raw) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  const result: CreatedAppletEntry[] = []
  for (const item of parsed) {
    if (!item || typeof item !== "object") continue
    const obj = item as Record<string, unknown>
    if (typeof obj.appletId !== "string" || obj.appletId.length === 0) continue
    if (typeof obj.createdAt !== "number" || !Number.isFinite(obj.createdAt)) continue
    result.push({
      conversationId: typeof obj.conversationId === "string" ? obj.conversationId : null,
      appletId: obj.appletId,
      createdAt: obj.createdAt,
    })
  }
  return result
}

const pruneStale = (entries: CreatedAppletEntry[], now: number): CreatedAppletEntry[] =>
  entries.filter(e => now - e.createdAt < STALE_AFTER_MS)

const writeEntries = (storage: Storage, entries: CreatedAppletEntry[]): void => {
  try {
    if (entries.length === 0) storage.removeItem(STORAGE_KEY)
    else storage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // best-effort
  }
}

export const recordCreatedApplet = ({ conversationId, appletId }: RecordInput): void => {
  const storage = getStorage()
  if (!storage) return
  const now = Date.now()
  const existing = pruneStale(parseEntries(storage.getItem(STORAGE_KEY)), now).filter(e => e.appletId !== appletId)
  const next = [...existing, { conversationId, appletId, createdAt: now }].slice(-MAX_ENTRIES)
  writeEntries(storage, next)
}

export const consumeMatchingAppletEntry = (appletId: string): CreatedAppletEntry | null => {
  const storage = getStorage()
  if (!storage) return null
  const now = Date.now()
  const fresh = pruneStale(parseEntries(storage.getItem(STORAGE_KEY)), now)
  const match = fresh.find(e => e.appletId === appletId) ?? null
  const remaining = match ? fresh.filter(e => e !== match) : fresh
  writeEntries(storage, remaining)
  return match
}
